import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import "../../assets/css/product-studio-edit.css";
import "../../assets/css/marketing-meta-ads.css";
import NotificationComponent from "../../components/Alert/alert";
import {
  useCreateMetaProductSetMutation,
  useDeleteMetaProductSetMutation,
  useFetchMetaProductSetsQuery,
  useUpdateMetaProductSetMutation,
} from "../../api/marketingApi";
import { useLanguage } from "../../contexts/LanguageContext";
import { useTranslation } from "../../hooks/useTranslation";
import { Btn, ConfirmDialog, Icon, Menu, Notice, cx } from "../catalogProductStudio/edit/StudioUi";
import { FeedStatus, SetRowSkeletons, ThumbStack } from "./MetaAdsUi";
import ProductSetDetailsDrawer from "./ProductSetDetailsDrawer";
import {
  FEED_STATE_META,
  fill,
  formatExactTime,
  formatRelativeTime,
  getApiStatus,
  getFeedStateKey,
  humanizeMetaAdsError,
  plural,
  productSetPath,
} from "./metaAdsModel";

const MetaAdsHeader = ({ tt, onCreate, showCreate }) => (
  <header className="mka-page-head">
    <div className="mka-page-head__text">
      <p className="mka-eyebrow">
        <span>{tt("eyebrowMarketing")}</span>
        <Icon name="chevronRight" size={12} />
        <span>{tt("eyebrowMetaAds")}</span>
      </p>
      <h1 className="mka-page-head__title">{tt("pageTitle")}</h1>
      <p className="mka-page-head__subtitle">{tt("pageSubtitle")}</p>
    </div>
    {showCreate ? (
      <div className="mka-page-head__actions">
        <Btn variant="primary" icon="plus" onClick={onCreate}>
          {tt("createSet")}
        </Btn>
      </div>
    ) : null}
  </header>
);

const CatalogReadLine = ({ lastMetaReadAt, tt, language }) => (
  <p className="mka-catalog-line" title={tt("catalogReadSchedule")}>
    <Icon name="meta" size={16} />
    {lastMetaReadAt ? (
      <span title={formatExactTime(lastMetaReadAt, language)}>
        {fill(tt("catalogReadAt"), { time: formatRelativeTime(lastMetaReadAt, language, Date.now(), tt("justNow")) })}
      </span>
    ) : (
      <span>
        {tt("catalogNotReadYet")}
        <span className="mka-muted"> · {tt("catalogReadSchedule")}</span>
      </span>
    )}
  </p>
);

const EmptyProductSets = ({ tt, onCreate }) => (
  <section className="mka-empty-hero" aria-labelledby="mka-empty-title">
    <span className="mka-empty-hero__icon" aria-hidden="true">
      <Icon name="layers" size={26} />
    </span>
    <h2 id="mka-empty-title" className="mka-empty-hero__title">
      {tt("emptyTitle")}
    </h2>
    <p className="mka-empty-hero__copy">{tt("emptyCopy")}</p>
    <ol className="mka-steps">
      {["emptyStep1", "emptyStep2", "emptyStep3"].map((key, index) => (
        <li key={key} className="mka-steps__item">
          <span className="mka-steps__number">{index + 1}</span>
          <span>{tt(key)}</span>
        </li>
      ))}
    </ol>
    <Btn variant="primary" icon="plus" onClick={onCreate}>
      {tt("createSet")}
    </Btn>
  </section>
);

const ProductSetRow = ({ productSet, tt, language, onRename, onDelete }) => {
  const stateKey = getFeedStateKey(productSet);
  const notReaching = productSet.selectionCount - productSet.reachingCount;
  const updated = formatRelativeTime(productSet.productsUpdatedAt, language, Date.now(), tt("justNow"));

  return (
    <li className="mka-set">
      <ThumbStack urls={productSet.previewImageUrls} count={productSet.selectionCount} />
      <div className="mka-set__main">
        <Link to={productSetPath(productSet.id)} className="mka-set__link">
          <span className="mka-set__name" title={productSet.name}>
            {productSet.name}
          </span>
        </Link>
        {productSet.description ? <span className="mka-set__description">{productSet.description}</span> : null}
        {notReaching > 0 ? (
          <span className="mka-set__warning">
            <Icon name="alert" size={13} />
            {plural(tt, "notReachingShort", notReaching)}
          </span>
        ) : null}
      </div>
      <div className="mka-set__count">
        <strong>{productSet.selectionCount}</strong>
        <span>{tt(productSet.selectionCount === 1 ? "selectionWordOne" : "selectionWordOther")}</span>
      </div>
      <div className="mka-set__state">
        <FeedStatus stateKey={stateKey} tt={tt} title={tt(FEED_STATE_META[stateKey].hintKey)} />
        <span className="mka-set__updated" title={formatExactTime(productSet.productsUpdatedAt, language)}>
          <span className="mka-set__count-inline">{plural(tt, "selectionCount", productSet.selectionCount)} · </span>
          {fill(tt("updatedAgo"), { time: updated })}
        </span>
      </div>
      <div className="mka-set__actions">
        <Menu
          label={fill(tt("setActionsFor"), { name: productSet.name })}
          items={[
            { label: tt("menuRename"), icon: "edit", onClick: () => onRename(productSet) },
            { label: tt("menuDelete"), icon: "trash", tone: "danger", onClick: () => onDelete(productSet) },
          ]}
        />
        <Icon name="chevronRight" size={18} className="mka-set__chevron" />
      </div>
    </li>
  );
};

const MetaAdsProductSets = () => {
  const t = useTranslation();
  const tt = useCallback((key) => t(`metaAds.${key}`), [t]);
  const { language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const notificationRef = useRef(null);

  const { data, isLoading, isFetching, isError, error, refetch } = useFetchMetaProductSetsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const [createProductSet] = useCreateMetaProductSetMutation();
  const [updateProductSet] = useUpdateMetaProductSetMutation();
  const [deleteProductSet, { isLoading: isDeleting }] = useDeleteMetaProductSetMutation();

  const [drawer, setDrawer] = useState({ open: false, mode: "create", productSet: null });
  const [pendingDelete, setPendingDelete] = useState(null);

  const notify = (message, type = "success") => notificationRef.current?.showNotification(message, type);
  const productSets = data?.productSets || [];

  // A product set page that deleted itself hands its confirmation over here.
  const handoffToast = location.state?.toast;
  useEffect(() => {
    if (!handoffToast) return;
    notificationRef.current?.showNotification(handoffToast, "success");
    navigate(location.pathname, { replace: true, state: null });
  }, [handoffToast]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmitDetails = async (values) => {
    if (drawer.mode === "edit") {
      await updateProductSet({ id: drawer.productSet.id, ...values }).unwrap();
      setDrawer({ open: false, mode: "create", productSet: null });
      notify(tt("toastUpdated"));
      return;
    }
    const created = await createProductSet(values).unwrap();
    setDrawer({ open: false, mode: "create", productSet: null });
    navigate(productSetPath(created.id), { state: { justCreated: true } });
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteProductSet(pendingDelete.id).unwrap();
      notify(fill(tt("toastDeleted"), { name: pendingDelete.name }));
      setPendingDelete(null);
    } catch (deleteError) {
      notify(humanizeMetaAdsError(deleteError, tt, "errDelete"), "danger");
    }
  };

  const openCreate = () => setDrawer({ open: true, mode: "create", productSet: null });
  const forbidden = isError && getApiStatus(error) === 403;

  let body;
  if (isLoading) {
    body = (
      <div className="mka-panel" aria-busy="true" aria-label={tt("loadingSets")}>
        <SetRowSkeletons />
      </div>
    );
  } else if (forbidden) {
    body = (
      <Notice tone="warning" icon="lock" title={tt("forbiddenTitle")}>
        {tt("forbiddenCopy")}
      </Notice>
    );
  } else if (isError) {
    body = (
      <Notice
        tone="danger"
        title={tt("loadSetsErrorTitle")}
        action={
          <Btn variant="secondary" size="sm" icon="refresh" onClick={() => refetch()} disabled={isFetching}>
            {tt("retry")}
          </Btn>
        }
      >
        {humanizeMetaAdsError(error, tt, "loadSetsErrorCopy")}
      </Notice>
    );
  } else if (!productSets.length) {
    body = <EmptyProductSets tt={tt} onCreate={openCreate} />;
  } else {
    body = (
      <section className={cx("mka-panel", isFetching && "is-refreshing")} aria-labelledby="mka-sets-title">
        <header className="mka-panel__head">
          <h2 id="mka-sets-title" className="mka-panel__title">
            {tt("setsHeading")}
            <span className="mka-count">{productSets.length}</span>
          </h2>
          <CatalogReadLine lastMetaReadAt={data?.catalog?.lastMetaReadAt} tt={tt} language={language} />
        </header>
        <ul className="mka-sets">
          {productSets.map((productSet) => (
            <ProductSetRow
              key={productSet.id}
              productSet={productSet}
              tt={tt}
              language={language}
              onRename={(set) => setDrawer({ open: true, mode: "edit", productSet: set })}
              onDelete={setPendingDelete}
            />
          ))}
        </ul>
      </section>
    );
  }

  return (
    <div className="content pse mka">
      <NotificationComponent ref={notificationRef} />
      <MetaAdsHeader tt={tt} onCreate={openCreate} showCreate={!forbidden && !isError && productSets.length > 0} />
      {body}

      <ProductSetDetailsDrawer
        isOpen={drawer.open}
        mode={drawer.mode}
        initialValues={drawer.productSet}
        onClose={() => setDrawer({ open: false, mode: "create", productSet: null })}
        onSubmit={handleSubmitDetails}
        tt={tt}
      />

      <ConfirmDialog
        isOpen={Boolean(pendingDelete)}
        tone="danger"
        title={fill(tt("deleteTitle"), { name: pendingDelete?.name || "" })}
        confirmLabel={isDeleting ? tt("deleting") : tt("deleteConfirm")}
        cancelLabel={tt("cancel")}
        isBusy={isDeleting}
        onCancel={() => setPendingDelete(null)}
        onConfirm={handleConfirmDelete}
      >
        {pendingDelete ? (
          <>
            <p>{tt("deleteCopyProducts")}</p>
            <p className="mka-muted">{tt("deleteCopyMeta")}</p>
          </>
        ) : null}
      </ConfirmDialog>
    </div>
  );
};

export default MetaAdsProductSets;
